// 2. Création des cibles en se basant sur les positions visibles

type Zone = {
  ZF: string[];
  ZP: string[];
  ZV: string[];
};

type Target = {
  position: string;
  size: number;
  gif: string;
};

type Salve = {
  targets: Target[];
};

/**
 * Génère les cibles pour le "Champ Profond d'Identification" en se basant sur
 * les zones de la grille visible (définies par visibleColumns et visibleRows).
 * Les cibles sont créées en respectant le nombre de salves et restent centrées.
 *
 * @param zoneData Les zones de positions (avec formats "A1", "B2", …)
 * @param numSalves Nombre de salves (groupes de cibles)
 * @param nombreDeCiblesParHemichamp [min, max] cibles par hémichamp (gauche/droite)
 * @param gifs Liste de GIFs à utiliser pour les cibles
 * @param sizes Liste des tailles possibles pour les cibles (ex.: [1,2,3])
 * @param visibleColumns Les indices des colonnes visibles (ex.: [2,3,…,13])
 * @param visibleRows Les indices des lignes visibles (ex.: [1,2,…,10])
 * @returns Une liste de salves contenant les cibles générées
 */
export function generateChampProfondIdentificationTargets(
  zoneData: Zone,
  numSalves: number,
  nombreDeCiblesParHemichamp: [number, number],
  gifs: string[],
  sizes = [1, 2, 3],
  visibleColumns: number[],
  visibleRows: number[]
): Salve[] {
  // console.log("params", zoneData, numSalves, nombreDeCiblesParHemichamp, gifs, sizes, visibleColumns, visibleRows, "window.innerWidth", window.innerWidth, "window.innerHeight", window.innerHeight);
  console.log('gifs length:', gifs.length, gifs);
  // Calcul des bornes visibles
  const visibleMinCol = Math.min(...visibleColumns);
  const visibleMaxCol = Math.max(...visibleColumns);
  const visibleMinRow = Math.min(...visibleRows);
  const visibleMaxRow = Math.max(...visibleRows);

  // Calcul du milieu de la zone visible pour séparer gauche et droite
  const visibleMid = Math.floor((visibleMinCol + visibleMaxCol) / 2);

  // Vérifie si une position (ex.: "B2") est entièrement visible
  function isPositionVisible(pos: string): boolean {
    const col = pos.charCodeAt(0) - 65;
    const row = parseInt(pos.slice(1)) - 1;
    return visibleColumns.includes(col) && visibleRows.includes(row);
  }

  // Filtrer les positions d'un tableau pour ne garder que celles visibles
  function filterVisiblePositions(positions: string[]): string[] {
    return positions.filter(isPositionVisible);
  }

  // On filtre chacune des zones pour ne conserver que les positions visibles
  const filteredZF = filterVisiblePositions(zoneData.ZF);
  const filteredZP = filterVisiblePositions(zoneData.ZP);
  const filteredZV = filterVisiblePositions(zoneData.ZV);

  // Zones fusionnées pour le côté gauche (généralement ZF + ZP) et zone alternative ZV
  const zones = { ZP_ZF: [...filteredZF, ...filteredZP], ZV: filteredZV };

  // Petite fonction utilitaire pour choisir un élément aléatoire d'un tableau
  function getRandomElement<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  /**
   * Tente de générer une cible à partir d'une position donnée.
   * On vérifie que le carré de côté "size" tient entièrement dans la zone visible
   * et qu'il n'empiète pas sur une zone déjà occupée.
   */
  function generateTarget(
    position: string,
    size: number,
    gif: string,
    zone: string[],
    occupiedPositions: Set<string>
  ): Target | null {
    const col = position.charCodeAt(0) - 65;
    const row = parseInt(position.slice(1)) - 1;

    // Vérifier que le carré de taille "size" est bien dans la zone visible
    if (
      col < visibleMinCol ||
      row < visibleMinRow ||
      col + size - 1 > visibleMaxCol ||
      row + size - 1 > visibleMaxRow
    ) {
      return null;
    }

    const newPositions = new Set<string>();
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        const checkCol = col + i;
        const checkRow = row + j;
        const checkPos = String.fromCharCode(65 + checkCol) + (checkRow + 1);
        // La position doit appartenir à la zone et ne pas être déjà occupée
        if (!zone.includes(checkPos) || occupiedPositions.has(checkPos))
          return null;
        newPositions.add(checkPos);
      }
    }
    // Marquer ces positions comme occupées
    newPositions.forEach((pos) => occupiedPositions.add(pos));
    return { position, size, gif };
  }

  // Création d'une salve (groupe de cibles)
  function generateSalve(): Salve {
    const targets: Target[] = [];
    const occupiedPositions = new Set<string>();

    // Détermination aléatoire du nombre de cibles à générer pour chaque hémichamp
    const leftTargetCount = getRandomElement(
      [
        ...Array(
          nombreDeCiblesParHemichamp[1] - nombreDeCiblesParHemichamp[0] + 1
        ).keys(),
      ].map((i) => i + nombreDeCiblesParHemichamp[0])
    );
    const rightTargetCount = getRandomElement(
      [
        ...Array(
          nombreDeCiblesParHemichamp[1] - nombreDeCiblesParHemichamp[0] + 1
        ).keys(),
      ].map((i) => i + nombreDeCiblesParHemichamp[0])
    );

    // Génération des cibles pour le côté gauche (positions dont la colonne est inférieure ou égale à visibleMid)
    for (let i = 0; i < leftTargetCount; i++) {
      let target: Target | null = null;
      // Sélection aléatoire entre zones ZP_ZF et ZV (en pondérant par exemple)
      const selectedZone = Math.random() < 0.625 ? zones.ZP_ZF : zones.ZV;

      let attempts = 0;
      while (!target && attempts < 10) {
        const possiblePositions = selectedZone.filter((pos) => {
          const posCol = pos.charCodeAt(0) - 65;
          return posCol <= visibleMid;
        });
        if (possiblePositions.length === 0) break;
        const position = getRandomElement(possiblePositions);
        const size = getRandomElement(sizes);
        const gif = getRandomElement(gifs);
        target = generateTarget(
          position,
          size,
          gif,
          selectedZone,
          occupiedPositions
        );
        attempts++;
      }
      if (target) targets.push(target);
    }

    // Génération des cibles pour le côté droit (positions dont la colonne est supérieure à visibleMid)
    for (let i = 0; i < rightTargetCount; i++) {
      let target: Target | null = null;
      const selectedZone = Math.random() < 0.625 ? zones.ZP_ZF : zones.ZV;

      let attempts = 0;
      while (!target && attempts < 10) {
        const possiblePositions = selectedZone.filter((pos) => {
          const posCol = pos.charCodeAt(0) - 65;
          return posCol > visibleMid;
        });
        if (possiblePositions.length === 0) break;
        const position = getRandomElement(possiblePositions);
        const size = getRandomElement(sizes);
        const gif = getRandomElement(gifs);
        target = generateTarget(
          position,
          size,
          gif,
          selectedZone,
          occupiedPositions
        );
        attempts++;
      }
      if (target) targets.push(target);
    }
    return { targets };
  }
  const salveReturned = Array.from({ length: numSalves }, generateSalve);
  const salveReturnedFiltered = salveReturned.filter((salve) => {
    // au moins une cible par hémichamp
    const leftTargets = salve.targets.filter((target) => {
      const col = target.position.charCodeAt(0) - 65;
      return col <= visibleMid;
    });
    const rightTargets = salve.targets.filter((target) => {
      const col = target.position.charCodeAt(0) - 65;
      return col > visibleMid;
    });
    return leftTargets.length > 0 && rightTargets.length > 0;
  });
  // Générer le nombre de salves demandées
  console.log("Generated salves Identification:", salveReturnedFiltered);
  return salveReturnedFiltered;
}

// // Exemples d'utilisation
// const zoneData: Zone = {
//   ZF: ["A1", "B1", "C1", "A2", "B2", "C2"],
//   ZP: ["D1", "E1", "F1", "D2", "E2", "F2"],
//   ZV: ["A3", "G1", "H1", "I1", "A4", "G2", "H2", "I2"],
// };

// const gifs = ["Geometric1", "Geometric2", "Bar1", "Bar2", "Circle1", "Circle2"];

// const numSalves = 40;
// const nombreDeCiblesParHemichamp = [1, 6];

// const salves = generateChampProfondIdentificationTargets(zoneData, numSalves, nombreDeCiblesParHemichamp, gifs);

// console.log(JSON.stringify(salves, null, 2));
